import { z } from 'zod';
import { type CertificateStatus, certificateStatus } from '../certificates.js';
import { addDays, addMonths, daysBetween, parseLocalDate } from '../local-date.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import { ATTACHMENT_MIME_TYPES, MAX_ATTACHMENT_BYTES } from './attachments.js';
import {
  flag,
  integer,
  localDate,
  option,
  optionalDate,
  optionalLines,
  optionalText,
  optionalUuid,
  text,
  uuid,
} from './fields.js';

/**
 * Gemi sertifikaları ve sörvey pencereleri (plan M7 Task 22; ADR 0029 K28–K30). Sertifika,
 * sörvey ve klas kayıtları ofisin; vize, sörveyör teyidi ve gemide düzenlenen sertifika geminin
 * (K28). Saf modül: bugün (gemi yerel tarihi) parametreyle gelir.
 */

/** "Expiring" eşiği (referans ekran "Expiring (≤ 30 days)"; şirket ayarı) (K30). */
export const VESSEL_CERTIFICATE_EXPIRING_DAYS = 30;

/** Otomatik hatırlatma kademeleri (gün); kişisel hatırlatıcı yoktur (K30). */
export const CERTIFICATE_REMINDER_DAYS = [90, 60, 30] as const;
export type CertificateReminderStage = (typeof CERTIFICATE_REMINDER_DAYS)[number] | 0;

/**
 * Sertifika türünün sörvey şeması (K29): HSSC (yıllık, ara, yenileme) [SOLAS I/7–I/10, HSSC];
 * SMC, ISPS ve MLC (2. ile 3. yıl dönümü arası ara doğrulama) [ISM 13.8, ISPS A/19.1.1,
 * MLC A5.1.3]; DOC (yıllık doğrulama) [ISM 13.4]; pencere yok.
 */
export const SURVEY_SCHEMES = ['hssc', 'ism_smc', 'ism_doc', 'isps', 'mlc', 'none'] as const;
export type SurveyScheme = (typeof SURVEY_SCHEMES)[number];

/** Sertifika ağacının kökleri (referans ekran "Certificate Tracking"). */
export const VESSEL_CERTIFICATE_CATEGORIES = [
  'statutory',
  'class',
  'safety',
  'environmental',
  'flag_port',
  'other',
] as const;
export type VesselCertificateCategory = (typeof VESSEL_CERTIFICATE_CATEGORIES)[number];

/** Survey Schedule satırları (PMS belgesi §19) ve klas sörveyleri. */
export const VESSEL_SURVEY_KINDS = [
  'annual',
  'intermediate',
  'special',
  'bottom',
  'boiler',
  'safety_equipment',
  'radio',
  'tailshaft',
  'other',
] as const;
export type VesselSurveyKind = (typeof VESSEL_SURVEY_KINDS)[number];

/** Sertifikanın hesaplanan pencere türleri. */
export const SURVEY_WINDOW_KINDS = ['annual', 'intermediate', 'renewal'] as const;
export type SurveyWindowKind = (typeof SURVEY_WINDOW_KINDS)[number];

/** Vizenin sörvey türü: yıllık, ara ya da ek (ek denetim pencereyi karşılamaz). */
export const ENDORSEMENT_KINDS = ['annual', 'intermediate', 'additional'] as const;
export type EndorsementKind = (typeof ENDORSEMENT_KINDS)[number];

/** Klas bulgusu: klas şartı (CoC), memorandum, tavsiye. */
export const CLASS_FINDING_KINDS = ['coc', 'memorandum', 'recommendation'] as const;
export type ClassFindingKind = (typeof CLASS_FINDING_KINDS)[number];

/** Pencere genişliği: yıl dönümünden ya da bitişten önce/sonra 3 ay (K29). */
export const SURVEY_WINDOW_MONTHS = 3;

/** Taranmış sertifika sayfası: PDF ya da görüntü; ek sınırıyla aynı (K15). */
export const CERTIFICATE_PAGE_MIME_TYPES = ATTACHMENT_MIME_TYPES;
export const MAX_CERTIFICATE_PAGE_BYTES = MAX_ATTACHMENT_BYTES;

/**
 * Sertifika ve klas uçlarının hata kodları (M7d). Ekranlar (Task 27a, 27b) her birini iki dilde
 * anlatır; o zaman `packages/ui/src/locales.test.ts` bu listeyi de denetler.
 */
export const VESSEL_CERTIFICATE_ERROR_CODES = [
  'INVALID_CERTIFICATE_TYPE',
  'INVALID_CERTIFICATE',
  'INVALID_CERTIFICATE_PAGE',
  'INVALID_SURVEY',
  'INVALID_CLASS_FINDING',
  'INVALID_CLASS_ITEM',
  'INVALID_CE_AUTHORIZATION',
  'INVALID_ENDORSEMENT',
  'INVALID_CLASS_CONFIRMATION',
  'INVALID_ISSUANCE',
  'PAGE_FILE_FIXED',
  'CERTIFICATE_TYPE_IN_USE',
  'EXPIRY_BEFORE_ISSUE',
  'OVERRIDE_REASON_REQUIRED',
  'WINDOW_ORDER',
  'DUE_BEFORE_LAST',
  'DUE_BEFORE_IMPOSED',
  'CLOSED_BEFORE_IMPOSED',
  'VALID_TO_BEFORE_FROM',
  'DUPLICATE_TYPE_CODE',
  'DUPLICATE_ITEM_CODE',
  'DUPLICATE_ITEM',
  'UNKNOWN_CERTIFICATE_TYPE',
  'UNKNOWN_CERTIFICATE',
  'UNKNOWN_ISSUANCE',
  'ISSUANCE_ALREADY_LINKED',
  'RENEWAL_CYCLE',
  'CERTIFICATE_DELETED',
  'UNKNOWN_USER',
  'UNKNOWN_EQUIPMENT',
  'UNKNOWN_WORK_RECORD',
  'CE_NOT_CREDITED',
  'CLASS_ITEM_MISMATCH',
  'ALREADY_CONFIRMED',
  'RECORD_VOIDED',
  'CONFIRMED_BEFORE_SURVEY',
  'CE_SURVEY_NOT_ALLOWED',
  'CE_NOT_AUTHORIZED',
  'UNKNOWN_BV_ITEM',
  'ENDORSEMENT_OUTSIDE_WINDOW',
  'UPLOAD_PURPOSE_MISMATCH',
  'FILE_TYPE_MISMATCH',
  'FILE_TOO_LARGE',
  'EMPTY_FILE',
  'MISSING_COLUMN',
  'FUTURE_DATE',
  'OFFICE_IS_WRITER',
  'SHIP_IS_WRITER',
] as const;

/* ---------- Durum (K28, K30) ---------- */

export interface VesselCertificateState {
  status: CertificateStatus;
  /** Bitişe kalan gün (bitiş günü 0); süresiz belgede `null`. */
  daysLeft: number | null;
}

/**
 * Sertifikanın durumu, gemi yerel tarihine göre. Bitiş günü geçerlidir; ertesi gün dolmuştur.
 * Personel belgesinin `certificateStatus` fonksiyonu eşik parametresiyle kullanılır.
 */
export function vesselCertificateStatus(
  expiryDate: string | null,
  today: string,
  expiringDays = VESSEL_CERTIFICATE_EXPIRING_DAYS,
): VesselCertificateState {
  return {
    status: certificateStatus(expiryDate, today, expiringDays),
    daysLeft: expiryDate === null ? null : daysBetween(today, expiryDate),
  };
}

/** Ulaşılan hatırlatma kademesi (90, 60, 30); dolmuş belgede 0, henüz erkense `null` (K30). */
export function reminderStage(
  expiryDate: string | null,
  today: string,
): CertificateReminderStage | null {
  if (expiryDate === null) return null;
  const left = daysBetween(today, expiryDate);
  if (left < 0) return 0;
  let stage: CertificateReminderStage | null = null;
  for (const days of CERTIFICATE_REMINDER_DAYS) if (left <= days) stage = days;
  return stage;
}

export interface TrackedCertificateInput {
  id: string;
  typeId: string;
  expiryDate: string | null;
  renewedFromId: string | null;
  onBoardIssuanceId: string | null;
}

export interface TrackedIssuanceInput {
  id: string;
  typeId: string;
  replacesCertificateId: string | null;
  expiryDate: string | null;
}

export interface TrackedCertificate extends VesselCertificateState {
  id: string;
  typeId: string;
  /** Ofisin sertifikası ya da gemide düzenlenen ve ofis kaydı henüz gelmemiş sertifika. */
  source: 'office' | 'on_board';
  /** Yenilenmiş ya da gemide yenisi düzenlenmiş sertifika: geçmişte kalır, sayılmaz. */
  superseded: boolean;
  /** "Valid (issued on board, office entry pending)" (K28). */
  officeEntryPending: boolean;
}

/**
 * Takip edilen sertifikalar (K28): ofis sertifikaları ve ofis kaydı henüz gelmemiş gemide
 * düzenlenen sertifikalar. Ofis sertifikası gemideki kayda `on_board_issuance_id` ile bağlanınca
 * gemi satırı listeden çıkar ve durumu etkilemez. Yenilenen (`renewed_from_id`) ya da gemide yerine
 * yenisi düzenlenen sertifika geçmişte kalır (`superseded`).
 */
export function trackedCertificates(
  certificates: readonly TrackedCertificateInput[],
  issuances: readonly TrackedIssuanceInput[],
  today: string,
  expiringDays = VESSEL_CERTIFICATE_EXPIRING_DAYS,
): TrackedCertificate[] {
  const linked = new Set(certificates.map((c) => c.onBoardIssuanceId).filter((id) => id !== null));
  const pending = issuances.filter((issuance) => !linked.has(issuance.id));
  const replaced = new Set<string>();
  for (const certificate of certificates) {
    if (certificate.renewedFromId !== null) replaced.add(certificate.renewedFromId);
  }
  // Gemide yerine yenisi düzenlenen sertifika, ofis kaydı bağlandıktan sonra da geçmişte kalır
  for (const issuance of issuances) {
    if (issuance.replacesCertificateId !== null) replaced.add(issuance.replacesCertificateId);
  }
  return [
    ...certificates.map((certificate) => ({
      id: certificate.id,
      typeId: certificate.typeId,
      source: 'office' as const,
      superseded: replaced.has(certificate.id),
      officeEntryPending: false,
      ...vesselCertificateStatus(certificate.expiryDate, today, expiringDays),
    })),
    ...pending.map((issuance) => ({
      id: issuance.id,
      typeId: issuance.typeId,
      source: 'on_board' as const,
      superseded: false,
      officeEntryPending: true,
      ...vesselCertificateStatus(issuance.expiryDate, today, expiringDays),
    })),
  ];
}

/** Özet kartları: yalnız geçmişte kalmamış sertifikalar (referans ekran: Valid, Expiring, Overdue, Total). */
export function certificateSummary(tracked: readonly TrackedCertificate[]) {
  const current = tracked.filter((c) => !c.superseded);
  return {
    valid: current.filter((c) => c.status === 'valid').length,
    expiring: current.filter((c) => c.status === 'expiring').length,
    overdue: current.filter((c) => c.status === 'expired').length,
    total: current.length,
  };
}

/** Yenileme zinciri, eskiden yeniye (dokuman-kutuphanesi.md §6). Döngü olsa da sonlanır. */
export function renewalChain(
  certificates: readonly { id: string; renewedFromId: string | null }[],
  id: string,
): string[] {
  const byId = new Map(certificates.map((c) => [c.id, c]));
  const next = new Map<string, string>();
  for (const certificate of [...certificates].sort((a, b) => (a.id < b.id ? -1 : 1))) {
    if (certificate.renewedFromId !== null && !next.has(certificate.renewedFromId)) {
      next.set(certificate.renewedFromId, certificate.id);
    }
  }
  const seen = new Set<string>([id]);
  const back: string[] = [];
  let cursor = byId.get(id)?.renewedFromId ?? null;
  while (cursor !== null && !seen.has(cursor)) {
    seen.add(cursor);
    back.unshift(cursor);
    cursor = byId.get(cursor)?.renewedFromId ?? null;
  }
  const forward: string[] = [];
  let ahead = next.get(id);
  while (ahead !== undefined && !seen.has(ahead)) {
    seen.add(ahead);
    forward.push(ahead);
    ahead = next.get(ahead);
  }
  return [...back, id, ...forward];
}

/* ---------- Sörvey pencereleri (K29) ---------- */

export interface SurveyOverride {
  nextSurvey: string | null;
  windowFrom: string | null;
  windowTo: string | null;
  reason: string | null;
}

export interface CertificateDates {
  issueDate: string;
  expiryDate: string | null;
  /** Yıl dönümü; boşsa bitiş tarihinin gün ve ayı [SOLAS I/2(n)]. */
  anniversaryDate: string | null;
  /** Ofisin klas ya da bayrak durum raporundan girdiği sıradaki sörvey (gerekçeli). */
  override: SurveyOverride | null;
}

export interface SurveyWindow {
  kind: SurveyWindowKind;
  /** Vade: yıl dönümü, ara sörveyde pencerenin dayandığı yıl dönümü, yenilemede bitiş. */
  due: string;
  from: string;
  to: string;
  source: 'computed' | 'override';
  reason: string | null;
}

export interface EndorsementLike {
  surveyKind: string;
  endorsedOn: string;
}

function dateOf(year: number, month: number, day: number): string {
  const first = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-01`;
  const last = parseLocalDate(addDays(addMonths(first, 1), -1)).day;
  return addDays(first, Math.min(day, last) - 1);
}

/** Düzenleme ile bitiş arasındaki yıl dönümleri (bitiş günü yenilemedir). */
function anniversaryDates(certificate: CertificateDates): string[] {
  const { issueDate, expiryDate } = certificate;
  if (expiryDate === null) return [];
  const base = parseLocalDate(certificate.anniversaryDate ?? expiryDate);
  const result: string[] = [];
  for (let year = parseLocalDate(issueDate).year; year <= parseLocalDate(expiryDate).year; year++) {
    const date = dateOf(year, base.month, base.day);
    if (date > issueDate && date < expiryDate) result.push(date);
  }
  return result;
}

const around = (due: string) => ({
  from: addMonths(due, -SURVEY_WINDOW_MONTHS),
  to: addMonths(due, SURVEY_WINDOW_MONTHS),
});

const KIND_ORDER: Record<SurveyWindowKind, number> = { annual: 0, intermediate: 1, renewal: 2 };

/**
 * Sertifikanın hesaplanan bütün pencereleri, bitişe göre sıralı (K29):
 * - `hssc`: her yıl dönümü ±3 ay yıllık; 2. yıl dönümünden 3 ay önce ile 3. yıl dönümünden 3 ay
 *   sonra arası ara sörvey; bitişten önceki 3 ay yenileme [SOLAS I/7–I/10, HSSC];
 * - `ism_smc`, `isps`, `mlc`: 2. ile 3. yıl dönümü arası ara doğrulama ya da denetim
 *   [ISM 13.8, ISPS A/19.1.1, MLC A5.1.3]; bitişten önceki 3 ay yenileme;
 * - `ism_doc`: her yıl dönümü ±3 ay yıllık doğrulama [ISM 13.4]; bitişten önceki 3 ay yenileme;
 * - `none` ya da bitişsiz sertifika: pencere yok.
 * Kısa süreli sertifikada (üçten az yıl dönümü) ara pencere yoktur.
 */
export function surveyWindows(certificate: CertificateDates, scheme: SurveyScheme): SurveyWindow[] {
  const { expiryDate } = certificate;
  if (expiryDate === null || scheme === 'none') return [];
  const anniversaries = anniversaryDates(certificate);
  const windows: SurveyWindow[] = [];
  const push = (kind: SurveyWindowKind, due: string, from: string, to: string) =>
    windows.push({ kind, due, from, to, source: 'computed', reason: null });
  if (scheme === 'hssc' || scheme === 'ism_doc') {
    for (const date of anniversaries) push('annual', date, around(date).from, around(date).to);
  }
  const [, second, third] = anniversaries;
  if (second !== undefined && third !== undefined) {
    if (scheme === 'hssc') push('intermediate', second, around(second).from, around(third).to);
    if (scheme === 'ism_smc' || scheme === 'isps' || scheme === 'mlc') {
      push('intermediate', third, second, third);
    }
  }
  push('renewal', expiryDate, addMonths(expiryDate, -SURVEY_WINDOW_MONTHS), expiryDate);
  return windows.sort(
    (a, b) =>
      (a.to < b.to ? -1 : a.to > b.to ? 1 : 0) ||
      (a.due < b.due ? -1 : a.due > b.due ? 1 : 0) ||
      KIND_ORDER[a.kind] - KIND_ORDER[b.kind],
  );
}

/** Vize bu pencereyi karşılar mı: aynı tür ve pencerenin içinde. HSSC ara sörveyi o yılın yıllığını da karşılar. */
function satisfies(window: SurveyWindow, endorsement: EndorsementLike, scheme: SurveyScheme) {
  const inside = window.from <= endorsement.endorsedOn && endorsement.endorsedOn <= window.to;
  const kindMatches =
    endorsement.surveyKind === window.kind ||
    (scheme === 'hssc' && window.kind === 'annual' && endorsement.surveyKind === 'intermediate');
  return inside && kindMatches && window.kind !== 'renewal';
}

export type NextSurvey = SurveyWindow & { state: 'upcoming' | 'open' | 'overdue' };

function stateOf(window: SurveyWindow, today: string): NextSurvey['state'] {
  if (today > window.to) return 'overdue';
  return today >= window.from ? 'open' : 'upcoming';
}

function overrideWindow(
  certificate: CertificateDates,
  computed: SurveyWindow | undefined,
): SurveyWindow | null {
  const override = certificate.override;
  if (!override) return null;
  const due = override.nextSurvey ?? override.windowTo ?? override.windowFrom;
  if (due === null) return null;
  const kind = computed?.kind ?? 'annual';
  return {
    kind,
    due,
    from: override.windowFrom ?? addMonths(due, -SURVEY_WINDOW_MONTHS),
    to: override.windowTo ?? (kind === 'annual' ? addMonths(due, SURVEY_WINDOW_MONTHS) : due),
    source: 'override',
    reason: override.reason,
  };
}

/**
 * Sıradaki sörvey: vizeyle karşılanmamış ilk pencere (bitişe göre). Son vizeden önce kapanan
 * pencereler geride kalmış sayılır: vize bloğu sıralıdır. Karşılanmamış ve kapanmış pencere
 * `overdue`'dur. Ofisin gerekçeyle girdiği tarih hesaplananın önüne geçer (K29).
 */
export function nextSurvey(
  certificate: CertificateDates,
  scheme: SurveyScheme,
  endorsements: readonly EndorsementLike[],
  today: string,
): NextSurvey | null {
  const latest = endorsements.reduce<string | null>(
    (max, e) => (max === null || e.endorsedOn > max ? e.endorsedOn : max),
    null,
  );
  const computed = surveyWindows(certificate, scheme).find(
    (window) =>
      (latest === null || window.to >= latest) &&
      !endorsements.some((e) => satisfies(window, e, scheme)),
  );
  const chosen = overrideWindow(certificate, computed) ?? computed;
  return chosen ? { ...chosen, state: stateOf(chosen, today) } : null;
}

/**
 * Türün bugüne göre geçerli ya da sıradaki penceresi (K29). Ofisin girdiği tarih sıradaki sörveyin
 * türündeyse onun yerine geçer; başka türün penceresi hesaplanmış kalır.
 */
export function surveyWindow(
  certificate: CertificateDates,
  kind: SurveyWindowKind,
  scheme: SurveyScheme,
  today: string,
  endorsements: readonly EndorsementLike[] = [],
): SurveyWindow | null {
  const next = nextSurvey(certificate, scheme, endorsements, today);
  if (next?.source === 'override' && next.kind === kind) {
    const { state: _state, ...window } = next;
    return window;
  }
  const ofKind = surveyWindows(certificate, scheme).filter((w) => w.kind === kind);
  return ofKind.find((w) => w.to >= today) ?? ofKind.at(-1) ?? null;
}

/**
 * Vizenin karşıladığı pencere (K29): pencerenin içindeki vize yıllık (ya da ara) sörveyi
 * karşılar; dışındaki kaydedilir ama karşılamaz ve uyarı verir. Ek denetim pencere aramaz.
 */
export function endorsementCredit(
  certificate: CertificateDates,
  scheme: SurveyScheme,
  endorsement: EndorsementLike,
): { satisfies: SurveyWindow | null; warning: 'ENDORSEMENT_OUTSIDE_WINDOW' | null } {
  if (endorsement.surveyKind === 'additional') return { satisfies: null, warning: null };
  const windows = surveyWindows(certificate, scheme);
  const override = overrideWindow(certificate, undefined);
  const found =
    windows.find((w) => w.kind === endorsement.surveyKind && satisfies(w, endorsement, scheme)) ??
    (override && override.from <= endorsement.endorsedOn && endorsement.endorsedOn <= override.to
      ? override
      : undefined);
  return found
    ? { satisfies: found, warning: null }
    : { satisfies: null, warning: 'ENDORSEMENT_OUTSIDE_WINDOW' };
}

/** Yıl dönümüne bağlı türler ±3 ay; diğerleri vadeden önceki 3 ay. */
const AROUND_DUE: ReadonlySet<VesselSurveyKind> = new Set([
  'annual',
  'intermediate',
  'safety_equipment',
  'radio',
]);

/**
 * Survey Schedule satırının hesaplanan penceresi (PMS belgesi §19; K29): yıllık, ara, emniyet
 * teçhizatı ve telsiz sörveyleri vade ±3 ay; özel (yenileme), havuz, kazan, şaft ve diğerleri
 * vadeden önceki 3 ay (daha sıkı okuma; ADR 0029 Ek: M7d). Vade yoksa pencere yok.
 */
export function surveyScheduleWindow(
  kind: VesselSurveyKind,
  nextDueOn: string | null,
): { from: string; to: string } | null {
  if (nextDueOn === null) return null;
  return AROUND_DUE.has(kind)
    ? around(nextDueOn)
    : { from: addMonths(nextDueOn, -SURVEY_WINDOW_MONTHS), to: nextDueOn };
}

/* ---------- Varsayılan sertifika ağacı (gemi-sertifika-takibi.jpeg) ---------- */

export interface VesselCertificateTypeSeed {
  code: string;
  name: string;
  category: VesselCertificateCategory;
  /** Ağacın ara düğümü (ör. SOLAS, MARPOL); boşsa tür doğrudan kategorinin altında. */
  subCategory: string | null;
  surveyScheme: SurveyScheme;
  sortOrder: number;
}

const TREE: readonly (readonly [
  string,
  string,
  VesselCertificateCategory,
  string | null,
  SurveyScheme,
])[] = [
  ['SOLAS-SE', 'Safety Equipment', 'statutory', 'SOLAS', 'hssc'],
  ['SOLAS-LSA', 'Life Saving Appliances', 'statutory', 'SOLAS', 'hssc'],
  ['SOLAS-FF', 'Fire Fighting', 'statutory', 'SOLAS', 'hssc'],
  ['SOLAS-RADIO', 'Radio & GMDSS', 'statutory', 'SOLAS', 'hssc'],
  ['SOLAS-ISPS', 'ISPS', 'statutory', 'SOLAS', 'isps'],
  // MARPOL düğümü referans ekranda kapalıdır; ekleri sertifikalarıyla (IOPP, NLS, ISPP, IAPP)
  ['MARPOL-I', 'Annex I (Oil)', 'statutory', 'MARPOL', 'hssc'],
  ['MARPOL-II', 'Annex II (NLS)', 'statutory', 'MARPOL', 'hssc'],
  // ISPP'de yıllık ve ara sörvey yoktur; yalnız bitiş izlenir
  ['MARPOL-IV', 'Annex IV (Sewage)', 'statutory', 'MARPOL', 'none'],
  ['MARPOL-VI', 'Annex VI (Air)', 'statutory', 'MARPOL', 'hssc'],
  ['STAT-MLC', 'MLC', 'statutory', null, 'mlc'],
  ['STAT-OTHER', 'Other Statutory', 'statutory', null, 'none'],
  ['CLASS-CLASS', 'Class', 'class', null, 'hssc'],
  ['CLASS-STAT', 'Statutory Class (IACS)', 'class', null, 'hssc'],
  ['CLASS-SURVEY', 'Class Survey', 'class', null, 'hssc'],
  ['SAFETY-ISM', 'ISM', 'safety', null, 'ism_smc'],
  ['SAFETY-ISPS', 'ISPS', 'safety', null, 'isps'],
  ['SAFETY-SMS', 'SMS', 'safety', null, 'none'],
  ['SAFETY-SE', 'Safety Equipment', 'safety', null, 'hssc'],
  ['ENV-IOPP', 'IOPP', 'environmental', null, 'hssc'],
  ['ENV-GARBAGE', 'Garbage Management', 'environmental', null, 'none'],
  ['ENV-DCS', 'Air Emission (IMO DCS)', 'environmental', null, 'none'],
  ['FLAG-STATE', 'Flag State', 'flag_port', null, 'none'],
  ['FLAG-PSC', 'Port State Control', 'flag_port', null, 'none'],
  ['OTHER-INS', 'Insurance', 'other', null, 'none'],
  // Şirketin DOC'unun gemideki kopyası: yıllık doğrulama [ISM 13.4]
  ['OTHER-DOC', 'Document of Compliance', 'other', null, 'ism_doc'],
  ['OTHER-MISC', 'Miscellaneous', 'other', null, 'none'],
];

/** Varsayılan sertifika türleri, ağaç sırasıyla; yeni şirkete tohumlanır. */
export const DEFAULT_VESSEL_CERTIFICATE_TYPES: readonly VesselCertificateTypeSeed[] = TREE.map(
  ([code, name, category, subCategory, surveyScheme], index) => ({
    code,
    name,
    category,
    subCategory,
    surveyScheme,
    sortOrder: (index + 1) * 10,
  }),
);

/* ---------- Form şemaları (RHF + Zod; ofis, gemi ve sunucu ortak) ---------- */

function result<T>(parsed: z.ZodSafeParseResult<T>): ParseResult<T> {
  return parsed.success
    ? { ok: true, value: parsed.data }
    : { ok: false, issues: toIssues(parsed.error) };
}

function future(path: string, value: string | null, today: string | undefined): SchemaIssue[] {
  return today !== undefined && value !== null && value > today
    ? [{ path, code: 'FUTURE_DATE' }]
    : [];
}

const SHA256 = /^[0-9a-f]{64}$/;

export const vesselCertificateTypeInputSchema = z.strictObject({
  code: text(40),
  name: text(120),
  category: option(VESSEL_CERTIFICATE_CATEGORIES),
  subCategory: optionalText(60),
  surveyScheme: option(SURVEY_SCHEMES),
  sortOrder: integer(0, 1_000_000)
    .nullish()
    .transform((value) => value ?? 0),
});
export type VesselCertificateTypeInput = z.output<typeof vesselCertificateTypeInputSchema>;

export function parseVesselCertificateTypeInput(
  input: unknown,
): ParseResult<VesselCertificateTypeInput> {
  return result(vesselCertificateTypeInputSchema.safeParse(input));
}

export const vesselCertificateInputSchema = z
  .strictObject({
    typeId: uuid,
    number: text(60),
    name: text(200),
    issuedBy: text(120),
    issueDate: localDate,
    expiryDate: optionalDate,
    anniversaryDate: optionalDate,
    nextSurveyOverride: optionalDate,
    windowFromOverride: optionalDate,
    windowToOverride: optionalDate,
    overrideReason: optionalText(500),
    renewedFromId: optionalUuid,
    onBoardIssuanceId: optionalUuid,
    remarks: optionalLines(2000),
  })
  .superRefine((input, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', message, path: [path] });
    if (input.expiryDate !== null && input.expiryDate < input.issueDate) {
      issue('expiryDate', 'EXPIRY_BEFORE_ISSUE');
    }
    // Hesaplanan pencerenin üstüne yazma gerekçe ister (K29)
    const overridden = [input.nextSurveyOverride, input.windowFromOverride, input.windowToOverride];
    if (overridden.some((value) => value !== null) && input.overrideReason === null) {
      issue('overrideReason', 'OVERRIDE_REASON_REQUIRED');
    }
    if (
      input.windowFromOverride !== null &&
      input.windowToOverride !== null &&
      input.windowFromOverride > input.windowToOverride
    ) {
      issue('windowToOverride', 'WINDOW_ORDER');
    }
  });
export type VesselCertificateInput = z.output<typeof vesselCertificateInputSchema>;

/** Sertifika formu; `today` ofiste geminin yerel günüdür: düzenleme tarihi gelecekte olamaz. */
export function parseVesselCertificateInput(
  input: unknown,
  options: { today?: string } = {},
): ParseResult<VesselCertificateInput> {
  const parsed = result(vesselCertificateInputSchema.safeParse(input));
  if (!parsed.ok) return parsed;
  const issues = future('issueDate', parsed.value.issueDate, options.today);
  return issues.length > 0 ? { ok: false, issues } : parsed;
}

/** Sertifikanın pencere üstüne yazması (satırdan). */
export function overrideOf(input: {
  nextSurveyOverride: string | null;
  windowFromOverride: string | null;
  windowToOverride: string | null;
  overrideReason: string | null;
}): SurveyOverride | null {
  const { nextSurveyOverride, windowFromOverride, windowToOverride, overrideReason } = input;
  if (nextSurveyOverride === null && windowFromOverride === null && windowToOverride === null) {
    return null;
  }
  return {
    nextSurvey: nextSurveyOverride,
    windowFrom: windowFromOverride,
    windowTo: windowToOverride,
    reason: overrideReason,
  };
}

export const vesselSurveyInputSchema = z.strictObject({
  kind: option(VESSEL_SURVEY_KINDS),
  lastDoneOn: optionalDate,
  nextDueOn: optionalDate,
  windowFrom: optionalDate,
  windowTo: optionalDate,
  overrideReason: optionalText(500),
  remarks: optionalLines(2000),
});
export type VesselSurveyInput = z.output<typeof vesselSurveyInputSchema>;

/**
 * Survey Schedule satırı (K29): pencere verilmezse vadeden hesaplanır; hesaplanandan farklı
 * pencere gerekçe ister (`OVERRIDE_REASON_REQUIRED`).
 */
export function parseVesselSurveyInput(input: unknown): ParseResult<VesselSurveyInput> {
  const parsed = result(vesselSurveyInputSchema.safeParse(input));
  if (!parsed.ok) return parsed;
  const value = parsed.value;
  const computed = surveyScheduleWindow(value.kind, value.nextDueOn);
  const windowFrom = value.windowFrom ?? computed?.from ?? null;
  const windowTo = value.windowTo ?? computed?.to ?? null;
  const issues: SchemaIssue[] = [];
  if (value.lastDoneOn !== null && value.nextDueOn !== null && value.nextDueOn < value.lastDoneOn) {
    issues.push({ path: 'nextDueOn', code: 'DUE_BEFORE_LAST' });
  }
  if (windowFrom !== null && windowTo !== null && windowFrom > windowTo) {
    issues.push({ path: 'windowTo', code: 'WINDOW_ORDER' });
  }
  const differs = windowFrom !== (computed?.from ?? null) || windowTo !== (computed?.to ?? null);
  if (differs && value.overrideReason === null) {
    issues.push({ path: 'overrideReason', code: 'OVERRIDE_REASON_REQUIRED' });
  }
  return issues.length > 0
    ? { ok: false, issues }
    : { ok: true, value: { ...value, windowFrom, windowTo } };
}

export const classFindingInputSchema = z
  .strictObject({
    kind: option(CLASS_FINDING_KINDS),
    number: text(60),
    text: text(2000),
    imposedOn: localDate,
    dueOn: optionalDate,
    closedOn: optionalDate,
    equipmentId: optionalUuid,
  })
  .superRefine((input, ctx) => {
    if (input.dueOn !== null && input.dueOn < input.imposedOn) {
      ctx.addIssue({ code: 'custom', message: 'DUE_BEFORE_IMPOSED', path: ['dueOn'] });
    }
    if (input.closedOn !== null && input.closedOn < input.imposedOn) {
      ctx.addIssue({ code: 'custom', message: 'CLOSED_BEFORE_IMPOSED', path: ['closedOn'] });
    }
  });
export type ClassFindingInput = z.output<typeof classFindingInputSchema>;

export function parseClassFindingInput(
  input: unknown,
  options: { today?: string } = {},
): ParseResult<ClassFindingInput> {
  const parsed = result(classFindingInputSchema.safeParse(input));
  if (!parsed.ok) return parsed;
  const issues = [
    ...future('imposedOn', parsed.value.imposedOn, options.today),
    ...future('closedOn', parsed.value.closedOn, options.today),
  ];
  return issues.length > 0 ? { ok: false, issues } : parsed;
}

export const ceAuthorizationInputSchema = z
  .strictObject({
    userId: uuid,
    validFrom: localDate,
    validTo: optionalDate,
    declarationRef: text(120),
  })
  .superRefine((input, ctx) => {
    if (input.validTo !== null && input.validTo < input.validFrom) {
      ctx.addIssue({ code: 'custom', message: 'VALID_TO_BEFORE_FROM', path: ['validTo'] });
    }
  });
export type CeAuthorizationInput = z.output<typeof ceAuthorizationInputSchema>;

/** Başmühendisin yetki beyanı (Owner's attestation) [NR467 Pt A Ch 2 App 2 1.1.2]. */
export function parseCeAuthorizationInput(input: unknown): ParseResult<CeAuthorizationInput> {
  return result(ceAuthorizationInputSchema.safeParse(input));
}

export const certificatePageInputSchema = z.strictObject({
  fileName: text(255),
  mimeType: option(CERTIFICATE_PAGE_MIME_TYPES),
  sha256: z.string().regex(SHA256, 'INVALID_SHA256'),
  size: integer(1, MAX_CERTIFICATE_PAGE_BYTES),
});
export type CertificatePageInput = z.output<typeof certificatePageInputSchema>;

export function parseCertificatePageInput(input: unknown): ParseResult<CertificatePageInput> {
  return result(certificatePageInputSchema.safeParse(input));
}

export const endorsementInputSchema = z.strictObject({
  surveyKind: option(ENDORSEMENT_KINDS),
  endorsedOn: localDate,
  place: text(120),
  surveyor: text(120),
  remarks: optionalLines(2000),
});
export type EndorsementInput = z.output<typeof endorsementInputSchema>;

/** Vize (gemide; K28): gemi yerel günü gelecekte olamaz. */
export function parseEndorsementInput(
  input: unknown,
  options: { today?: string } = {},
): ParseResult<EndorsementInput> {
  const parsed = result(endorsementInputSchema.safeParse(input));
  if (!parsed.ok) return parsed;
  const issues = future('endorsedOn', parsed.value.endorsedOn, options.today);
  return issues.length > 0 ? { ok: false, issues } : parsed;
}

export const onBoardIssuanceInputSchema = z
  .strictObject({
    typeId: uuid,
    replacesCertificateId: optionalUuid,
    number: text(60),
    issuedBy: text(120),
    issueDate: localDate,
    expiryDate: localDate,
    shortTerm: flag(),
    remarks: optionalLines(2000),
  })
  .superRefine((input, ctx) => {
    if (input.expiryDate < input.issueDate) {
      ctx.addIssue({ code: 'custom', message: 'EXPIRY_BEFORE_ISSUE', path: ['expiryDate'] });
    }
  });
export type OnBoardIssuanceInput = z.output<typeof onBoardIssuanceInputSchema>;

/** Sörveyörün gemide düzenlediği yeni ya da kısa süreli sertifika (K28). */
export function parseOnBoardIssuanceInput(
  input: unknown,
  options: { today?: string } = {},
): ParseResult<OnBoardIssuanceInput> {
  const parsed = result(onBoardIssuanceInputSchema.safeParse(input));
  if (!parsed.ok) return parsed;
  const issues = future('issueDate', parsed.value.issueDate, options.today);
  return issues.length > 0 ? { ok: false, issues } : parsed;
}
